import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../components/ui/command";
import { toast } from "sonner";
import { format } from "date-fns";
import { Loader2, CheckCircle, AlertTriangle, Link2, Users, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

export default function JobDivaMatchingPage() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [linking, setLinking] = useState({});
  const [dateRange, setDateRange] = useState("14");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const today = format(new Date(), "yyyy-MM-dd");
  const getFromDate = () => {
    if (dateRange === "custom") return customFrom;
    const d = new Date();
    d.setDate(d.getDate() - parseInt(dateRange));
    return format(d, "yyyy-MM-dd");
  };
  const getToDate = () => {
    if (dateRange === "custom") return customTo;
    return today;
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(res.data.filter(u => u.is_active));
    } catch { /* ignore */ }
  };

  const fetchMatching = async () => {
    setLoading(true);
    try {
      const fromDate = getFromDate();
      const toDate = getToDate();
      if (!fromDate || !toDate) { toast.error("Select both dates"); setLoading(false); return; }
      const res = await axios.get(`${API}/jobdiva/recruiter-activity?from_date=${fromDate}&to_date=${toDate}`);
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to fetch JobDiva data");
    } finally {
      setLoading(false);
    }
  };

  const handleLink = async (userId, jobdivaId) => {
    setLinking(prev => ({ ...prev, [userId]: true }));
    try {
      await axios.put(`${API}/users/${userId}`, { jobdiva_id: jobdivaId });
      toast.success("JobDiva ID linked");
      fetchMatching();
      fetchUsers();
    } catch (err) {
      toast.error("Failed to link");
    } finally {
      setLinking(prev => ({ ...prev, [userId]: false }));
    }
  };

  const matched = data?.data || {};
  const unmatched = data?.unmatched || [];
  const matchedCount = Object.keys(matched).length;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            JobDiva Matching
          </h1>
          <p className="text-slate-600 mt-1">Match JobDiva recruiters to JTCRM users</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-44 h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Today</SelectItem>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="custom">Custom range</SelectItem>
            </SelectContent>
          </Select>
          {dateRange === "custom" && (
            <>
              <Input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} className="w-36 h-9" />
              <span className="text-slate-400">to</span>
              <Input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} className="w-36 h-9" />
            </>
          )}
          <Button onClick={fetchMatching} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Fetch & Match
          </Button>
        </div>
      </div>

      {data && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-700">{matchedCount}</p>
                    <p className="text-sm text-slate-500">Matched</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <AlertTriangle className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-amber-700">{unmatched.length}</p>
                    <p className="text-sm text-slate-500">Unmatched JD Recruiters</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <Users className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-indigo-700">{matchedCount + unmatched.length}</p>
                    <p className="text-sm text-slate-500">Total JD Recruiters</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Matched */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-700">
                <CheckCircle className="h-5 w-5" />
                Matched ({matchedCount})
              </CardTitle>
              <CardDescription>These JobDiva recruiters are linked to JTCRM users</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(matched).map(([userId, info]) => {
                  const user = users.find(u => u.id === userId);
                  return (
                    <div key={userId} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <div>
                        <p className="font-medium text-slate-800">{user?.full_name || userId}</p>
                        <p className="text-xs text-slate-500">{user?.email}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">{info.matched_by}</Badge>
                        <span className="text-sm text-slate-600">JD: {info.jd_name}</span>
                        <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                          S:{info.submittals} I:{info.interviews} H:{info.hires}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
                {matchedCount === 0 && <p className="text-sm text-slate-400 text-center py-4">No matches yet</p>}
              </div>
            </CardContent>
          </Card>

          {/* Unmatched */}
          {unmatched.length > 0 && (
            <Card className="border-amber-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-700">
                  <AlertTriangle className="h-5 w-5" />
                  Unmatched JD Recruiters ({unmatched.length})
                </CardTitle>
                <CardDescription>These JobDiva recruiters could not be matched. Manually link them to a JTCRM user below.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {unmatched.map((jd) => (
                    <div key={jd.jd_id} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg gap-3">
                      <div className="min-w-0">
                        <p className="font-medium text-slate-800">{jd.name}</p>
                        <p className="text-xs text-slate-500">JD ID: {jd.jd_id} — S:{jd.submittals} I:{jd.interviews} H:{jd.hires}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" size="sm" className="w-48 h-8 text-xs justify-start">
                              <Link2 className="h-3 w-3 mr-1.5" />
                              Link to user...
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-56 p-0" align="end">
                            <Command>
                              <CommandInput placeholder="Search users..." />
                              <CommandList>
                                <CommandEmpty>No user found.</CommandEmpty>
                                <CommandGroup>
                                  {users.map(u => (
                                    <CommandItem
                                      key={u.id}
                                      value={u.full_name}
                                      onSelect={() => handleLink(u.id, jd.jd_id)}
                                    >
                                      <span className="text-sm">{u.full_name}</span>
                                      <span className="text-xs text-slate-400 ml-1">({u.email})</span>
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {!data && !loading && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Link2 className="h-12 w-12 text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-700">Click "Fetch & Match" to start</h3>
            <p className="text-slate-500 mt-1 text-sm">This will fetch today's recruiter data from JobDiva and match it to JTCRM users</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
